import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../supabaseClient'
import { C, st, formatDate } from '../../theme'
import { Modal, ScrollTable, PageHeader, EmptyState, StatCard, Pagination, PAGE_SIZE, SearchSelect } from '../../components/UI'
import { useAppPrefs } from '../../AppPrefs'
import { makeT } from '../../i18n'

// ── Standalone form to avoid re-mount input issues ──
function ShipmentFormBody({ form, setForm, items, setItems, products, productOptions, drivers, customers, customerOptions, _ }) {
  const [showManualCustomer, setShowManualCustomer] = useState(false)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div>
          <label style={st.label}>{_('shipNoLabel')} · No *</label>
          <input style={st.input} value={form.shipment_no} placeholder="SHP-001" onChange={e => { const v = e.target.value; setForm(f => ({ ...f, shipment_no: v })) }} />
        </div>
        <div>
          <label style={st.label}>{_('shipCustomerLabel')} · Customer *</label>
          {!showManualCustomer ? (
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <SearchSelect
                  options={customerOptions}
                  value={form.customer_name}
                  onChange={val => setForm(f => ({ ...f, customer_name: val }))}
                  placeholder={_('shipSearchCustomer')}
                  allowFreeText
                />
              </div>
              <button type="button" style={{ ...st.btn('default'), padding: '0 10px', fontSize: 11, minHeight: 44, whiteSpace: 'nowrap' }} onClick={() => setShowManualCustomer(true)}>
                手動輸入
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ ...st.input, flex: 1 }} value={form.customer_name} placeholder="輸入客戶名稱" onChange={e => { const v = e.target.value; setForm(f => ({ ...f, customer_name: v })) }} />
              <button type="button" style={{ ...st.btn('default'), padding: '0 10px', fontSize: 11, minHeight: 44, whiteSpace: 'nowrap' }} onClick={() => setShowManualCustomer(false)}>
                選清單
              </button>
            </div>
          )}
        </div>
        <div>
          <label style={st.label}>{_('shipDateLabel')} · Date</label>
          <input type="date" style={st.input} value={form.ship_date} onChange={e => setForm(f => ({ ...f, ship_date: e.target.value }))} />
        </div>
        <div>
          <label style={st.label}>備注 · Note</label>
          <input style={st.input} value={form.note} placeholder="備注" onChange={e => { const v = e.target.value; setForm(f => ({ ...f, note: v })) }} />
        </div>
      </div>

      {/* Driver selection */}
      <div style={{ padding: '12px 16px', background: '#f8fafc', borderRadius: 10, border: `1.5px solid ${C.border}` }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>{_('shipDriverSection')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div>
            <label style={st.label}>{_('shipSelectDriver')} · Driver</label>
            <select style={st.input} value={form.driver_id || ''} onChange={e => {
              const driver = drivers.find(d => d.id === e.target.value)
              setForm(f => ({ ...f, driver_id: e.target.value, driver_name: driver?.name || '', license_plate: driver?.license_plate || f.license_plate }))
            }}>
              <option value="">-- 選擇司機 --</option>
              {drivers.filter(d => d.is_active).map(d => (
                <option key={d.id} value={d.id}>{d.name}{d.license_plate ? ` · ${d.license_plate}` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={st.label}>{_('shipDriverNameLabel')} · Driver Name</label>
            <input style={st.input} value={form.driver_name || ''} placeholder="手動填寫或選擇上方" onChange={e => { const v = e.target.value; setForm(f => ({ ...f, driver_name: v })) }} />
          </div>
          <div>
            <label style={st.label}>{_('shipLicensePlate')} · License Plate</label>
            <input style={st.input} value={form.license_plate || ''} placeholder="ABC-1234" onChange={e => { const v = e.target.value; setForm(f => ({ ...f, license_plate: v })) }} />
          </div>
        </div>
      </div>

      {/* Items */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>{_('shipItemsSection')} · Items</div>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: 2 }}>
              {i === 0 && <label style={st.label}>商品 · Product</label>}
              <SearchSelect
                options={productOptions}
                value={item.product_id}
                onChange={val => { const n = [...items]; n[i].product_id = val; setItems(n) }}
                placeholder="搜尋商品..."
              />
            </div>
            <div style={{ flex: 1 }}>
              {i === 0 && <label style={st.label}>數量 · Qty</label>}
              <input type="number" style={st.input} value={item.qty} onChange={e => { const n = [...items]; n[i].qty = e.target.value; setItems(n) }} placeholder="0" />
            </div>
            {items.length > 1 && (
              <button style={{ ...st.btn('danger'), padding: '10px 12px', minHeight: 44 }} onClick={() => setItems(items.filter((_, j) => j !== i))}>✕</button>
            )}
          </div>
        ))}
        <button style={{ ...st.btn('default'), padding: '7px 14px' }} onClick={() => setItems([...items, { product_id: '', qty: '' }])}>
          <span>+ 新增品項</span>
        </button>
      </div>
    </div>
  )
}

function DriverFormBody({ form, setForm, _ }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
      <div>
        <label style={st.label}>司機姓名 · Name *</label>
        <input style={st.input} value={form.name || ''} placeholder="司機姓名" onChange={e => { const v = e.target.value; setForm(f => ({ ...f, name: v })) }} />
      </div>
      <div>
        <label style={st.label}>車牌號碼 · License Plate</label>
        <input style={st.input} value={form.license_plate || ''} placeholder="ABC-1234" onChange={e => { const v = e.target.value; setForm(f => ({ ...f, license_plate: v })) }} />
      </div>
      <div>
        <label style={st.label}>電話 · Phone</label>
        <input style={st.input} value={form.phone || ''} placeholder="0912-345-678" onChange={e => { const v = e.target.value; setForm(f => ({ ...f, phone: v })) }} />
      </div>
      <div>
        <label style={st.label}>備注 · Note</label>
        <input style={st.input} value={form.note || ''} placeholder="備注" onChange={e => { const v = e.target.value; setForm(f => ({ ...f, note: v })) }} />
      </div>
      <div>
        <label style={st.label}>狀態</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 10, cursor: 'pointer', background: form.is_active ? C.greenBg : '#f1f5f9', border: `2px solid ${form.is_active ? C.greenBorder : C.border}`, minHeight: 52 }}>
          <input type="checkbox" checked={!!form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} style={{ width: 18, height: 18 }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: form.is_active ? C.green : C.muted }}>{form.is_active ? _('shipDriverEmployed') : _('shipDriverResigned')}</span>
        </label>
      </div>
    </div>
  )
}

export default function Shipping({ products, userProfile, showToast, addLog, onRefresh, isMobile, enabledModules = [] }) {
  const { lang } = useAppPrefs()
  const _ = makeT(lang)
  const isAdmin = userProfile?.role === 'admin'
  const pos = userProfile?.position || 'user'
  const hasShippingPerm = enabledModules.includes('shipping')
  const canManage = isAdmin || hasShippingPerm || ['factory_manager','senior_manager','section_chief','sales','sales_assistant'].includes(pos)
  const canDelete = isAdmin || enabledModules.includes('delete_records') || ['factory_manager','senior_manager'].includes(pos)

  const [activeTab, setActiveTab] = useState('shipments')
  const [shipments, setShipments] = useState([])
  const [drivers, setDrivers] = useState([])
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [showAddDriver, setShowAddDriver] = useState(false)
  const [editingDriver, setEditingDriver] = useState(null)
  const [page, setPage] = useState(1)
  const [driverPage, setDriverPage] = useState(1)
  const [saving, setSaving] = useState(false)
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterSearch, setFilterSearch] = useState('')
  const [selected, setSelected] = useState([])
  const [detailShipment, setDetailShipment] = useState(null)
  const [recSearch, setRecSearch] = useState('')
  const [recFilterStatus, setRecFilterStatus] = useState('all')
  const [recDateFrom, setRecDateFrom] = useState('')
  const [recDateTo, setRecDateTo] = useState('')
  const [recPage, setRecPage] = useState(1)

  const emptyForm = () => ({ shipment_no: '', customer_name: '', ship_date: '', note: '', driver_id: '', driver_name: '', license_plate: '' })
  const emptyDriverForm = () => ({ name: '', license_plate: '', phone: '', note: '', is_active: true })
  const [form, setForm] = useState(emptyForm())
  const [items, setItems] = useState([{ product_id: '', qty: '' }])
  const [driverForm, setDriverForm] = useState(emptyDriverForm())

  const load = async () => {
    setLoading(true)
    const cid = userProfile?.company_id
    let shipQ = supabase.from('shipments').select('*, shipment_items(*, products(name, unit))')
    if (cid) shipQ = shipQ.eq('company_id', cid)
    let drvQ = supabase.from('drivers').select('*')
    if (cid) drvQ = drvQ.eq('company_id', cid)
    let custQ = supabase.from('customers').select('id, name')
    if (cid) custQ = custQ.eq('company_id', cid)
    const [{ data: ships }, { data: drvs }, { data: custs }] = await Promise.all([
      shipQ.order('created_at', { ascending: false }).limit(500),
      drvQ.order('name'),
      custQ.order('name'),
    ])
    if (ships) setShipments(ships)
    if (drvs) setDrivers(drvs)
    if (custs) setCustomers(custs)
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  // Auto-generate shipment no
  useEffect(() => {
    if (showAdd && !form.shipment_no) {
      const date = new Date()
      const rocYear = date.getFullYear() - 1911
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const prefix = `SHP-${rocYear}${month}${day}`
      supabase.from('shipments').select('id', { count: 'exact', head: true }).like('shipment_no', `${prefix}%`)
        .then(({ count }) => {
          const seq = String((count || 0) + 1).padStart(2, '0')
          setForm(f => ({ ...f, shipment_no: `${prefix}-${seq}` }))
        })
    }
  }, [showAdd])

  const filtered = useMemo(() => shipments.filter(s => {
    if (filterStatus !== 'all' && s.status !== filterStatus) return false
    if (filterSearch && !s.customer_name?.includes(filterSearch) && !s.shipment_no?.includes(filterSearch)) return false
    return true
  }), [shipments, filterStatus, filterSearch])

  const recFiltered = useMemo(() => shipments.filter(s => {
    if (recFilterStatus !== 'all' && s.status !== recFilterStatus) return false
    if (recSearch && !s.customer_name?.includes(recSearch) && !s.shipment_no?.includes(recSearch) && !s.driver_name?.includes(recSearch)) return false
    if (recDateFrom && s.ship_date < recDateFrom) return false
    if (recDateTo && s.ship_date > recDateTo) return false
    return true
  }), [shipments, recFilterStatus, recSearch, recDateFrom, recDateTo])

  const recTotalPages = useMemo(() => Math.ceil(recFiltered.length / PAGE_SIZE), [recFiltered])
  const recPaged = useMemo(() => recFiltered.slice((recPage-1)*PAGE_SIZE, recPage*PAGE_SIZE), [recFiltered, recPage])
  const paged = useMemo(() => filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE), [filtered, page])
  const totalPages = useMemo(() => Math.ceil(filtered.length / PAGE_SIZE), [filtered])
  const pagedDrivers = useMemo(() => drivers.slice((driverPage-1)*PAGE_SIZE, driverPage*PAGE_SIZE), [drivers, driverPage])
  const totalDriverPages = useMemo(() => Math.ceil(drivers.length / PAGE_SIZE), [drivers])

  const { pending, shipped, cancelled } = useMemo(() => ({
    pending: shipments.filter(s => s.status === 'pending').length,
    shipped: shipments.filter(s => s.status === 'shipped').length,
    cancelled: shipments.filter(s => s.status === 'cancelled').length,
  }), [shipments])

  const activeDriverCount = useMemo(() => drivers.filter(d => d.is_active).length, [drivers])
  const productOptions = useMemo(() => products.map(p => ({ value: p.id, label: p.name, sub: `庫存: ${p.stock} ${p.unit}` })), [products])
  const customerOptions = useMemo(() => customers.map(c => ({ value: c.name, label: c.name })), [customers])

  const statusInfo = (s) => ({
    pending:   { label: _('shipStatusPending'),   badge: 'pending', color: C.yellow },
    shipped:   { label: _('shipStatusShipped'),   badge: 'done',    color: C.green },
    cancelled: { label: _('shipStatusCancelled'), badge: '',        color: C.muted },
  }[s] || { label: s, badge: '', color: C.muted })

  const handleAdd = async () => {
    if (!form.shipment_no || !form.customer_name) { showToast('請填寫出貨單號和客戶名稱', 'error'); return }
    if (items.some(i => !i.product_id || !i.qty)) { showToast('請填寫所有出貨品項', 'error'); return }
    setSaving(true)
    const { shipment_no, customer_name, ship_date, note, driver_id, driver_name, license_plate } = form
    const { data: shipData, error } = await supabase.from('shipments').insert({
      shipment_no, customer_name, ship_date, note,
      driver_id: driver_id || null, driver_name, license_plate,
      status: 'pending', operator: userProfile?.name,
      company_id: userProfile?.company_id || null,
    }).select().single()
    if (error) { setSaving(false); showToast('新增失敗：' + error.message, 'error'); return }
    await supabase.from('shipment_items').insert(
      items.filter(i => i.product_id && i.qty).map(i => {
        const p = products.find(p => p.id === i.product_id)
        return { shipment_id: shipData.id, product_id: i.product_id, product_name: p?.name || '', qty: parseInt(i.qty), unit: p?.unit || '', company_id: userProfile?.company_id || null }
      })
    )
    await addLog('新增出貨單', `出貨單 ${form.shipment_no}：${form.customer_name}`)
    setForm(emptyForm()); setItems([{ product_id: '', qty: '' }]); setShowAdd(false)
    showToast('✓ 出貨單已建立'); setSaving(false); load()
  }

  const handleUpdateStatus = async (id, shipment_no, status) => {
    if (status === 'shipped') {
      const shipment = shipments.find(s => s.id === id)
      const items = (shipment?.shipment_items || []).filter(i => i.product_id && i.qty > 0)
      if (items.length > 0) {
        const productIds = [...new Set(items.map(i => i.product_id))]
        const { data: prods } = await supabase.from('products').select('id, name, stock, unit').in('id', productIds)
        const prodMap = {}
        if (prods) prods.forEach(p => { prodMap[p.id] = p })
        const insufficient = items.filter(i => prodMap[i.product_id] && prodMap[i.product_id].stock < i.qty)
        if (insufficient.length > 0) {
          showToast(`庫存不足：${insufficient.map(i => prodMap[i.product_id]?.name || i.product_name).join('、')}，無法出貨`, 'error')
          return
        }
        await Promise.all(items.map(async item => {
          const prod = prodMap[item.product_id]
          if (!prod) return
          await Promise.all([
            supabase.from('products').update({ stock: prod.stock - item.qty, updated_at: new Date().toISOString() }).eq('id', item.product_id),
            supabase.from('records').insert({ product_id: item.product_id, type: 'out', qty: item.qty, note: `出貨出庫 · ${shipment_no}`, operator: userProfile?.name, customer_name: shipment?.customer_name || null, company_id: userProfile?.company_id || null }),
          ])
        }))
      }
    }
    const { error } = await supabase.from('shipments').update({ status, updated_at: new Date().toISOString() }).eq('id', id)
    if (error) { showToast('更新失敗：' + error.message, 'error'); return }
    setShipments(prev => prev.map(s => s.id === id ? { ...s, status } : s))
    await addLog('更新出貨狀態', `出貨單 ${shipment_no} → ${statusInfo(status).label}`)
    showToast('✓ 狀態已更新')
  }

  const handleDelete = async (id, shipment_no) => {
    if (!window.confirm(`確定刪除出貨單 ${shipment_no}？`)) return
    await supabase.from('shipments').delete().eq('id', id)
    await addLog('刪除出貨單', shipment_no)
    showToast('✓ 已刪除'); setSelected(prev => prev.filter(s => s !== id)); load()
  }

  const handleBatchDelete = async () => {
    if (!window.confirm(`確定刪除 ${selected.length} 筆？`)) return
    await supabase.from('shipments').delete().in('id', selected)
    await addLog('批量刪除出貨單', `${selected.length} 筆`)
    showToast(`✓ 已刪除 ${selected.length} 筆`); setSelected([]); load()
  }

  const handleSaveDriver = async () => {
    if (!driverForm.name) { showToast('請填寫司機姓名', 'error'); return }
    setSaving(true)
    const { id, created_at, ...rest } = driverForm
    if (editingDriver) {
      const { error } = await supabase.from('drivers').update(rest).eq('id', editingDriver.id)
      if (error) { showToast('更新失敗：' + error.message, 'error'); setSaving(false); return }
      showToast('✓ 司機資料已更新'); setEditingDriver(null)
    } else {
      const { error } = await supabase.from('drivers').insert({ ...rest, company_id: userProfile?.company_id || null }).select()
      if (error) { showToast('新增失敗：' + error.message, 'error'); setSaving(false); return }
      showToast('✓ 司機已新增'); setShowAddDriver(false)
    }
    setDriverForm(emptyDriverForm()); setSaving(false); load()
  }

  const handleDeleteDriver = async (id, name) => {
    if (!window.confirm(`確定刪除司機「${name}」？`)) return
    await supabase.from('drivers').delete().eq('id', id)
    showToast('✓ 已刪除'); load()
  }

  return (
    <div>
      <PageHeader title="出貨管理" en="Shipping Management" vi="Vận chuyển" />

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard icon="📋" value={shipments.length} label={_('shipStatTotal')} en="Total" />
        <StatCard icon="⏳" value={pending} label={_('shipStatPending')} en="Pending" warn={pending > 0} />
        <StatCard icon="✅" value={shipped} label={_('shipStatShipped')} en="Shipped" />
        <StatCard icon="🚛" value={activeDriverCount} label={_('shipStatDrivers')} en="Drivers" />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <button style={{ ...st.btn(activeTab === 'shipments' ? 'primary' : 'default'), padding: '10px 20px' }} onClick={() => setActiveTab('shipments')}>
          <span>{_('shipTabShipments')}</span>
          <span style={{ fontSize: 9, opacity: 0.5, display: 'block' }}>Shipments</span>
        </button>
        <button style={{ ...st.btn(activeTab === 'records' ? 'primary' : 'default'), padding: '10px 20px' }} onClick={() => setActiveTab('records')}>
          <span>{_('shipTabRecords')}</span>
          <span style={{ fontSize: 9, opacity: 0.5, display: 'block' }}>Records</span>
        </button>
        <button style={{ ...st.btn(activeTab === 'drivers' ? 'primary' : 'default'), padding: '10px 20px' }} onClick={() => setActiveTab('drivers')}>
          <span>{_('shipTabDrivers')}</span>
          <span style={{ fontSize: 9, opacity: 0.5, display: 'block' }}>Driver Management</span>
        </button>
      </div>

      {/* ── 出貨單 ── */}
      {activeTab === 'shipments' && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[['all','全部'],['pending','⏳ ' + _('shipStatusPending')],['shipped','✅ ' + _('shipStatusShipped')],['cancelled','✕ ' + _('shipStatusCancelled')]].map(([v,l]) => (
                <button key={v} style={{ ...st.btn(filterStatus === v ? 'primary' : 'default'), padding: '7px 14px' }} onClick={() => { setFilterStatus(v); setPage(1); setSelected([]) }}>
                  <span>{l}</span>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {isAdmin && selected.length > 0 && (
                <button style={{ ...st.btn('danger'), padding: '8px 14px' }} onClick={handleBatchDelete}>
                  <span>🗑 刪除({selected.length})</span>
                </button>
              )}
              <button style={st.btn()} onClick={() => { setShowAdd(!showAdd); setForm(emptyForm()); setItems([{ product_id: '', qty: '' }]) }}>
                <span>{showAdd ? '✕ ' + _('cancel') : _('shipNewShipment')}</span>
                <span style={{ fontSize: 9, opacity: 0.5, display: 'block' }}>{showAdd ? 'Cancel' : 'New'}</span>
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              style={{ ...st.input, width: 220 }}
              value={filterSearch}
              placeholder={_('shipSearchPh')}
              onChange={e => { setFilterSearch(e.target.value); setPage(1); setSelected([]) }}
            />
            {filterSearch && (
              <button style={{ ...st.btn('default'), padding: '8px 12px', fontSize: 12 }} onClick={() => { setFilterSearch(''); setPage(1) }}>✕ 清除</button>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: C.muted }}>{filtered.length} 筆</span>
          </div>

          {showAdd && (
            <div style={{ ...st.card, marginBottom: 16, border: `2px solid ${C.accent}` }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 16 }}>{_('shipFormTitle')} · New Shipment</div>
              <ShipmentFormBody form={form} setForm={setForm} items={items} setItems={setItems} products={products} productOptions={productOptions} drivers={drivers} customers={customers} customerOptions={customerOptions} _={_} />
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button style={{ ...st.btn(), flex: 1, padding: '14px' }} onClick={handleAdd} disabled={saving}>
                  <span>{saving ? '建立中...' : _('shipConfirmCreate')}</span>
                </button>
                <button style={{ ...st.btn('default'), padding: '14px 20px' }} onClick={() => setShowAdd(false)}>{_('cancel')}</button>
              </div>
            </div>
          )}

          <div style={st.card}>
            {isAdmin && filtered.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.muted }}>
                <input type="checkbox" checked={selected.length === paged.length && paged.length > 0} onChange={e => setSelected(e.target.checked ? paged.map(s => s.id) : [])} style={{ width: 16, height: 16 }} />
                <span>全選本頁</span>
              </div>
            )}
            <ScrollTable>
              <thead>
                <tr>
                  {isAdmin && <th style={st.th}></th>}
                  {[[_('shipThNo'),'No'],[_('shipThCustomer'),'Customer'],[_('shipThDriver'),'Driver'],['車牌','Plate'],['品項','Items'],[_('shipThDate'),'Date'],[_('shipThStatus'),'Status'],['操作','Action']].map(([zh,en]) => (
                    <th key={zh} style={st.th}><div>{zh}</div><div style={{ fontSize: 9, opacity: 0.45 }}>{en}</div></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 9 : 8}><EmptyState icon="🚚" text={_('shipNoShipments')} /></td></tr>
                ) : paged.map(s => {
                  const si = statusInfo(s.status)
                  return (
                    <tr key={s.id} style={{ background: selected.includes(s.id) ? C.accentDim : 'transparent' }}>
                      {isAdmin && (
                        <td style={st.td}><input type="checkbox" checked={selected.includes(s.id)} onChange={e => setSelected(prev => e.target.checked ? [...prev, s.id] : prev.filter(id => id !== s.id))} style={{ width: 16, height: 16 }} /></td>
                      )}
                      <td style={{ ...st.td, fontFamily: 'monospace', color: C.accent, fontWeight: 700 }}>{s.shipment_no}</td>
                      <td style={{ ...st.td, color: C.text, fontWeight: 600 }}>{s.customer_name}</td>
                      <td style={st.td}>{s.driver_name || '-'}</td>
                      <td style={{ ...st.td, fontFamily: 'monospace', fontSize: 12, color: C.muted }}>{s.license_plate || '-'}</td>
                      <td style={st.td}>
                        {s.shipment_items?.slice(0, 2).map(i => (
                          <div key={i.id} style={{ fontSize: 12, color: C.sub }}>{i.products?.name || i.product_name} × {i.qty}</div>
                        ))}
                        {s.shipment_items?.length > 2 && <div style={{ fontSize: 11, color: C.muted }}>...還有 {s.shipment_items.length - 2} 項</div>}
                      </td>
                      <td style={st.td}>{s.ship_date || '-'}</td>
                      <td style={st.td}><span style={{ ...st.badge(si.badge), fontSize: 12 }}>{si.label}</span></td>
                      <td style={st.td}>
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          <button style={{ ...st.btn('default'), padding: '5px 10px', fontSize: 11, minHeight: 36 }} onClick={() => setDetailShipment(s)}>
                            <span>{_('shipBtnDetail')}</span>
                          </button>
                          {s.status === 'pending' && canManage && (
                            <button style={{ ...st.btn('success'), padding: '5px 10px', fontSize: 11, minHeight: 36 }} onClick={() => handleUpdateStatus(s.id, s.shipment_no, 'shipped')}>
                              <span>{_('shipBtnConfirmShip')}</span>
                            </button>
                          )}
                          {s.status === 'pending' && canManage && (
                            <button style={{ ...st.btn('danger'), padding: '5px 10px', fontSize: 11, minHeight: 36 }} onClick={() => handleUpdateStatus(s.id, s.shipment_no, 'cancelled')}>
                              <span>{_('cancel')}</span>
                            </button>
                          )}
                          {canDelete && (
                            <button style={{ ...st.btn('danger'), padding: '5px 10px', fontSize: 11, minHeight: 36 }} onClick={() => handleDelete(s.id, s.shipment_no)}>
                              <span>刪除</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </ScrollTable>
            <Pagination page={page} total={totalPages} onChange={setPage} />
          </div>
        </>
      )}

      {/* ── 紀錄查詢 ── */}
      {activeTab === 'records' && (
        <div style={st.card}>
          {/* Status filter */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            {[['all','全部'],['pending','⏳ ' + _('shipStatusPending')],['shipped','✅ ' + _('shipStatusShipped')],['cancelled','✕ ' + _('shipStatusCancelled')]].map(([v,l]) => (
              <button key={v} style={{ ...st.btn(recFilterStatus === v ? 'primary' : 'default'), padding: '7px 14px' }} onClick={() => { setRecFilterStatus(v); setRecPage(1) }}>
                <span>{l}</span>
              </button>
            ))}
          </div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center', padding: '12px 14px', background: '#f8fafc', borderRadius: 10, border: `1.5px solid ${C.border}` }}>
            <div style={{ fontSize: 12, color: C.muted, fontWeight: 700 }}>篩選：</div>
            <input style={{ ...st.input, width: 180 }} value={recSearch} placeholder="🔍 客戶、單號、司機" onChange={e => { setRecSearch(e.target.value); setRecPage(1) }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="date" style={{ ...st.input, width: 140 }} value={recDateFrom} onChange={e => { setRecDateFrom(e.target.value); setRecPage(1) }} />
              <span style={{ fontSize: 13, color: C.muted }}>～</span>
              <input type="date" style={{ ...st.input, width: 140 }} value={recDateTo} onChange={e => { setRecDateTo(e.target.value); setRecPage(1) }} />
            </div>
            {(recSearch || recDateFrom || recDateTo || recFilterStatus !== 'all') && (
              <button style={{ ...st.btn('default'), padding: '7px 12px', fontSize: 12 }} onClick={() => { setRecSearch(''); setRecDateFrom(''); setRecDateTo(''); setRecFilterStatus('all'); setRecPage(1) }}>
                <span>✕ 清除</span>
              </button>
            )}
            <span style={{ marginLeft: 'auto', fontSize: 12, color: C.muted }}>共 {recFiltered.length} 筆</span>
          </div>

          <ScrollTable>
            <thead>
              <tr>{[[_('shipThNo')],[_('shipThCustomer')],[_('shipThDriver')],['車牌'],[_('shipThDate')],[_('shipThStatus')],['品項']].map(h => (
                <th key={h} style={st.th}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {recPaged.length === 0 ? (
                <tr><td colSpan={7}><EmptyState icon="🔍" text={_('shipNoRecords')} /></td></tr>
              ) : recPaged.map(s => {
                const si = statusInfo(s.status)
                return (
                  <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => setDetailShipment(s)}>
                    <td style={{ ...st.td, fontFamily: 'monospace', color: C.accent, fontWeight: 700 }}>{s.shipment_no}</td>
                    <td style={{ ...st.td, color: C.text, fontWeight: 600 }}>{s.customer_name}</td>
                    <td style={st.td}>{s.driver_name || '-'}</td>
                    <td style={{ ...st.td, fontFamily: 'monospace', fontSize: 12, color: C.muted }}>{s.license_plate || '-'}</td>
                    <td style={st.td}>{s.ship_date || '-'}</td>
                    <td style={st.td}><span style={{ ...st.badge(si.badge), fontSize: 12 }}>{si.label}</span></td>
                    <td style={st.td}>
                      {s.shipment_items?.slice(0, 2).map(i => (
                        <div key={i.id} style={{ fontSize: 12, color: C.sub }}>{i.products?.name || i.product_name} × {i.qty}</div>
                      ))}
                      {s.shipment_items?.length > 2 && <div style={{ fontSize: 11, color: C.muted }}>...還有 {s.shipment_items.length - 2} 項</div>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </ScrollTable>
          <Pagination page={recPage} total={recTotalPages} onChange={setRecPage} />
        </div>
      )}

      {/* ── 司機 / 車牌管理 ── */}
      {activeTab === 'drivers' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button style={st.btn()} onClick={() => { setShowAddDriver(!showAddDriver); setEditingDriver(null); setDriverForm(emptyDriverForm()) }}>
              <span>{showAddDriver ? '✕ ' + _('cancel') : _('shipAddDriver')}</span>
              <span style={{ fontSize: 9, opacity: 0.5, display: 'block' }}>{showAddDriver ? 'Cancel' : 'Add Driver'}</span>
            </button>
          </div>

          {showAddDriver && !editingDriver && (
            <div style={{ ...st.card, marginBottom: 16, border: `2px solid ${C.accent}` }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 16 }}>{_('shipAddDriverTitle')} · New Driver</div>
              <DriverFormBody form={driverForm} setForm={setDriverForm} _={_} />
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button style={{ ...st.btn(), flex: 1, padding: '14px' }} onClick={handleSaveDriver} disabled={saving}>
                  <span>{saving ? '儲存中...' : '確認新增'}</span>
                </button>
                <button style={{ ...st.btn('default'), padding: '14px 20px' }} onClick={() => setShowAddDriver(false)}>{_('cancel')}</button>
              </div>
            </div>
          )}

          <div style={st.card}>
            <ScrollTable>
              <thead>
                <tr>{[['姓名','Name'],['車牌','Plate'],['電話','Phone'],['備注','Note'],['狀態','Status'],['操作','Action']].map(([zh,en]) => (
                  <th key={zh} style={st.th}><div>{zh}</div><div style={{ fontSize: 9, opacity: 0.5 }}>{en}</div></th>
                ))}</tr>
              </thead>
              <tbody>
                {drivers.length === 0 ? (
                  <tr><td colSpan={6}><EmptyState icon="🚛" text={_('shipNoDrivers')} /></td></tr>
                ) : pagedDrivers.map(d => (
                  <tr key={d.id}>
                    <td style={{ ...st.td, fontWeight: 700, color: C.text }}>{d.name}</td>
                    <td style={{ ...st.td, fontFamily: 'monospace', color: C.accent }}>{d.license_plate || '-'}</td>
                    <td style={st.td}>{d.phone || '-'}</td>
                    <td style={{ ...st.td, color: C.muted }}>{d.note || '-'}</td>
                    <td style={st.td}><span style={{ ...st.badge(d.is_active ? 'ok' : ''), fontSize: 12 }}>{d.is_active ? _('shipDriverEmployed') : _('shipDriverResigned')}</span></td>
                    <td style={st.td}>
                      <div style={{ display: 'flex', gap: 5 }}>
                        <button style={{ ...st.btn('warning'), padding: '5px 10px', fontSize: 12, minHeight: 36 }} onClick={() => { setEditingDriver(d); setDriverForm({ ...d }) }}>
                          <span>編輯</span>
                        </button>
                        {isAdmin && (
                          <button style={{ ...st.btn('danger'), padding: '5px 10px', fontSize: 12, minHeight: 36 }} onClick={() => handleDeleteDriver(d.id, d.name)}>
                            <span>刪除</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ScrollTable>
            <Pagination page={driverPage} total={totalDriverPages} onChange={setDriverPage} />
          </div>
        </>
      )}

      {/* Driver Edit Modal */}
      {editingDriver && (
        <Modal title={`✏️ 編輯司機 — ${editingDriver.name}`} onClose={() => setEditingDriver(null)}>
          <DriverFormBody form={driverForm} setForm={setDriverForm} _={_} />
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button style={{ ...st.btn(), flex: 1, padding: '14px' }} onClick={handleSaveDriver} disabled={saving}>
              <span>{saving ? '更新中...' : '確認更新'}</span>
            </button>
            <button style={{ ...st.btn('default'), padding: '14px 20px' }} onClick={() => setEditingDriver(null)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* Detail Modal */}
      {detailShipment && (
        <Modal title={`${_('shipDetailTitle')} — ${detailShipment.shipment_no}`} onClose={() => setDetailShipment(null)}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 16 }}>
            {[
              ['客戶', detailShipment.customer_name],
              ['出貨日期', detailShipment.ship_date || '-'],
              ['司機', detailShipment.driver_name || '-'],
              ['車牌', detailShipment.license_plate || '-'],
              ['狀態', statusInfo(detailShipment.status).label],
              ['備注', detailShipment.note || '-'],
            ].map(([label, value]) => (
              <div key={label} style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: 10, border: `1.5px solid ${C.border}` }}>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 14, color: C.text, fontWeight: 700 }}>{value}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 10 }}>出貨品項</div>
          {detailShipment.shipment_items?.map(i => (
            <div key={i.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 14px', background: '#f8fafc', borderRadius: 8, marginBottom: 6, fontSize: 14 }}>
              <span style={{ color: C.text, fontWeight: 600 }}>{i.products?.name || i.product_name}</span>
              <span style={{ color: C.accent, fontWeight: 700 }}>× {i.qty} {i.products?.unit || i.unit}</span>
            </div>
          ))}
        </Modal>
      )}
    </div>
  )
}
